import { _, tinycolor } from "js/vendor";
import * as geom from "js/core/utilities/geom";
import { AssetType, ShapeType } from "common/constants";
import { BaseElement } from "js/elements/base/BaseElement";
import { Icon } from "js/elements/base/IconElement";
import { Picture, Logo } from "js/elements/base/PictureElement";
import { Asset } from "../../core/models/assets";
import { fitRectInCircle } from "js/core/utilities/geom";
import React, { Fragment } from "reactn";
import { getClipPath } from "../../core/utilities/svgHelpers";
import { VideoElement } from "js/elements/base/VideoElement";

class ContentElement extends BaseElement {
    get _canSelect() {
        return true;
    }

    get _canRollover() {
        return true;
    }

    get bindTo() {
        return this.options.bindTo || "content_value";
    }

    get contentType() {
        return this.model["content_type"];
    }

    get contentValue() {
        return this.model[this.bindTo];
    }

    get canDropImage() {
        return true;
    }

    get canHandlePaste() {
        return true;
    }

    get asset() {
        if (this.assetElement) {
            return this.assetElement.asset;
        }
    }

    get defaultAssetType() {
        return this.options.defaultAssetType || this.contentType || AssetType.IMAGE;
    }

    get hasAsset() {
        return this.asset != null;
    }

    getContentSize() {
        switch (this.assetElement.type) {
            case "Picture":
                if (this.asset && this.asset.getImageType() == AssetType.LOGO) {
                    return new geom.Size(200, 200);
                } else if (this.assetElement) {
                    return this.assetElement.mediaSize;
                } else {
                    return new geom.Size(200, 200);
                }
            case AssetType.ICON:
            case AssetType.LOGO:
            default:
                return new geom.Size(200, 200);
        }
    }

    get aspectRatio() {
        return "fill";
    }

    get constrainAspectRatio() {
        return this.asset.constrainAspectRatio || "fit";
    }

    get showDefaultOverlay() {
        return !this.hasValidAsset && !this.options.forceHideDefaultOverlay;
    }

    get hasValidAsset() {
        if (!this.contentType || this.contentType.equalsAnyOf(AssetType.IMAGE, AssetType.ICON, AssetType.LOGO)) {
            if (this.assetElement && this.assetElement instanceof Picture && this.assetElement.hasMedia == false) {
                return false;
            }
            return !_.isEmpty(this.contentValue);
        } else {
            return true;
        }
    }

    get selectionPadding() {
        return 0;
    }

    get isBackgroundVisible() {
        if (this.contentType == AssetType.ICON) {
            return true;
        }
        if (this.assetElement.hasAlpha) {
            return true;
        }
        if (this.options.allowImageScaling && this.assetElement.isScaledBelowFill) {
            return true;
        }
        return false;
    }

    get hasAlpha() {
        return this.hasAsset && (this.contentType === AssetType.ICON || this.assetElement.hasAlpha);
    }

    get assetType() {
        return this.contentType || this.options.defaultAssetType || AssetType.IMAGE;
    }

    get assetIconScale() {
        return null; // use style iconScale setting unless overridden
    }

    _build() {
        switch (this.assetType) {
            case AssetType.ICON:
                this.assetElement = this.addElement("asset", () => Icon, {
                    iconScale: this.assetIconScale
                });
                break;
            case AssetType.IMAGE:
                this.assetElement = this.addElement("asset", () => Picture, {
                    bindTo: this.bindTo,
                    allowScaleBelowFill: this.options.allowScaleBelowFill,
                    allowImageScaling: this.options.allowImageScaling,
                    isOnAuthoringCanvas: this.isOnAuthoringCanvas
                });
                break;
            case AssetType.LOGO:
                this.assetElement = this.addElement("asset", () => Logo, {
                    bindTo: this.bindTo,
                    defaultScale: this.options.defaultLogoScale || 0.9
                });
                break;
            case AssetType.VIDEO:
            case AssetType.STOCK_VIDEO:
                const {
                    assetProps: {
                        controls,
                        loop,
                        autoPlay,
                        duration,
                        start,
                        end,
                        speed,
                        muted,
                    },
                    content_value,
                } = this.model;
                this.assetElement = this.addElement("asset", () => VideoElement, {
                    bindTo: this.bindTo,
                    content_value,
                    controls,
                    loop,
                    autoPlay,
                    duration,
                    start,
                    end,
                    speed,
                    muted,
                });
                break;
        }
    }

    hitCheck(pt) {
        if (this.styles.decoration && this.styles.decoration.shape === ShapeType.CIRCLE) {
            let r = Math.min(this.calculatedSize.width, this.calculatedSize.height) / 2;
            return pt.minus(r, r).magnitude() <= r;
        } else {
            let w = this.calculatedSize.width;
            let h = this.calculatedSize.height;
            return pt.x <= w && pt.x >= 0 && pt.y <= h && pt.y >= 0;
        }
    }

    _calcProps(props, options = {}) {
        let { size, children, styles } = props;

        if (options.backgroundColor || (this.model.backgroundColor && this.model.backgroundColor != "none")) {
            if (this.decoration) {
                this.decoration.styles.fillColor = options.backgroundColor || this.model.backgroundColor;
            } else {
                this.createDecoration({
                    type: "frame",
                    shape: "rect",
                    fillColor: options.backgroundColor || this.model.backgroundColor
                });
            }
        } else if (this.asset && this.asset.get("hasSolidBackground") && this.asset.get("hasAlpha") == false) {
            if (this.decoration) {
                this.decoration.styles.fillColor = tinycolor(this.asset.get("imageBackgroundColor")).toHexString();
            } else {
                this.createDecoration({
                    type: "frame",
                    shape: "rect",
                    fillColor: tinycolor(this.asset.get("imageBackgroundColor")).toHexString()
                });
            }
        }

        // Always fitting icons
        let fitAsset = this.assetType === AssetType.ICON ? true : options.fitAsset;

        if (this.hasValidAsset &&
            (this.contentType == AssetType.IMAGE || this.contentType == AssetType.LOGO || this.contentType == AssetType.VIDEO || this.contentType == AssetType.STOCK_VIDEO) &&
            this.decoration &&
            this.decoration.styles.type == "frame" &&
            this.decoration.styles.strokeWidth
        ) {
            const hasStroke = !!this.decoration.styles.strokeColor && this.decoration.styles.strokeColor !== "none";
            // Default the frame to expand outward by one pixel by default so we cover the background with the image.
            let frameWidth = -1;
            // If we have stroke, then shrink the frame so we can draw the border
            if (hasStroke) {
                // We divide the stroke width when shrinking to ensure the image fills the inner gaps of the stroke.
                // Dividing by 2 isn't enough for octagon gaps, so we divide by 2.2 instead.
                frameWidth = Math.max(Math.floor(this.decoration.styles.strokeWidth / 2.2), 0);
            }

            // Decrease the inner radius by the strokeWidth to eliminate gaps in the corners.
            const innerRadius = Math.max(this.decoration.styles.cornerRadius - frameWidth, 0);
            const assetSize = size.deflate(frameWidth);
            const assetProps = children.setProps(this.assetElement, assetSize, Object.assign(options, { fitAsset }));
            assetProps.bounds = new geom.Rect(frameWidth, frameWidth, assetSize);
            assetProps.clipPath = getClipPath(this.decoration.styles.shape, new geom.Rect(0, 0, assetSize), innerRadius);
        } else {
            const assetProps = children.setProps(this.assetElement, size, Object.assign(options, { fitAsset }));
            assetProps.bounds = new geom.Rect(0, 0, assetProps.size);
        }

        return { size };
    }

    //     // clip an image asset with either the clip from any Decoration or a rect
    //     if (this.assetElement && this.assetElement instanceof Picture && this.hasAsset) {
    //
    //         return (
    //             <Fragment>
    //                 <clipPath id="clip-path">
    //                     <circle cx="40" cy="35" r="35"/>
    //                 </clipPath>
    //                 <g {...this.getRenderProps(layout, transition)} clip-path={`url(#clip-path)`}>
    //                     {this.assetElement.renderElement(layout.children.asset, transition)}
    //                 </g>
    //             </Fragment>
    //         );
    //     } else {
    //         return super._renderElement(layout, transition);
    //     }
    // }

    // _render(transition) {
    //     // clip an image asset with either the clip from any Decoration or a rect
    //     if (this.assetElement && this.assetElement instanceof Picture && this.hasAsset) {
    //         if (this.clip) {
    //             this.clip.remove();
    //             this.clip = null;
    //         }
    //
    //         if (this.customClip) {
    //             this.clip = this.customClip;
    //         } else if (this.decoration) {
    //             this.clip = this.decoration.getClip();
    //         }
    //         if (!this.clip) {
    //             this.clip = this.svg.rect();
    //         }
    //         this.assetElement.svg.clipWith(this.clip);
    //
    //         if (!this.customClip) {
    //             if (this.decoration && this.decoration.bounds) {
    //                 const bounds = new geom.Rect(
    //                     0, //this.decoration.bounds.left,
    //                     0, //this.decoration.bounds.top,
    //                     this.decoration.bounds.width - this.styles.padding.left - this.styles.padding.right,
    //                     this.decoration.bounds.height - this.styles.padding.top - this.styles.padding.bottom
    //                 );
    //                 this.clip.setBounds(bounds, transition);
    //             } else {
    //                 this.clip.setBounds(this.assetElement.bounds, transition);
    //             }
    //         }
    //     }
    // }
}

class LogoElement extends ContentElement {
    get _canSelect() {
        return true;
    }

    get _canRollover() {
        return true;
    }

    get defaultOverlayType() {
        return "ContentElementDefaultOverlay";
    }

    get userScale() {
        return this.model.userScale || 1;
    }

    get defaultScale() {
        return this._defaultScale;
    }

    get maxScale() {
        return 2;
    }

    get minScale() {
        return 0.5;
    }

    get defaultWidth() {
        return this.options.defaultWidth || 100;
    }

    get defaultHeight() {
        return this.options.defaultHeight || 100;
    }

    get fitWidth() {
        return Boolean(this.options.fitWidth);
    }

    get fitHeight() {
        return Boolean(this.options.fitHeight);
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        if (!this.hasValidAsset) {
            size = new geom.Size(this.defaultWidth, this.defaultHeight);
            let assetProps = children.setProps(this.assetElement, size);
            assetProps.bounds = new geom.Rect(0, 0, size);
            return { size };
        }

        if (this.canvas.layouter.isGenerating) {
            // if no decoration is defined in the style sheet and the logo asset doesn't have alpha, add a white box behind
            if (!this.decoration) {
                let frameColor = "white";
                if (this.assetElement.isTransparent) {
                    frameColor = this.model.backgroundColor || "none";
                }

                if (frameColor != "none") {
                    this.createDecoration({
                        type: "frame",
                        shape: "rect",
                        fillColor: frameColor
                    });
                }
            }
        }

        // fit the logo into the availableSize
        let logoSize;

        if (this.assetElement.mediaSize) {
            logoSize = this.assetElement.mediaSize;
        } else {
            logoSize = size.square();
        }

        if (this.fitWidth) {
            this._defaultScale = size.width / logoSize.width;
        } else if (this.fitHeight) {
            this._defaultScale = size.height / logoSize.height;
        } else {
            this._defaultScale = Math.min(size.width / logoSize.width, size.height / logoSize.height);
        }

        logoSize = logoSize.scale(this._defaultScale * this.userScale);

        let assetProps = children.setProps(this.assetElement, logoSize, Object.assign(options, { fitAsset: true }));
        assetProps.bounds = new geom.Rect(0, 0, logoSize);

        return { size: logoSize };
    }
}

class AdjustableContentElement extends ContentElement {
    get frameType() {
        if (this.model.frameType) {
            return this.model.frameType;
        } else {
            switch (this.canvas.getTheme().get("styleShape")) {
                case "rect":
                case "rounded_rect":
                    return "square";
                case "circle":
                    return "circle";
                case "none":
                    return "none";
                default:
                    return "circle";
            }
        }
    }

    get passThroughSelection() {
        return false;
    }

    get contentSize() {
        return this.model.contentSize || 50;
    }

    get minWidth() {
        return 20;
    }

    get maxWidth() {
        return 300;
    }

    get allowDecorationStyles() {
        return this.frameType != "none";
    }

    get decorationStyle() {
        if (this.allowDecorationStyles) {
            return this.model.decorationStyle;
        } else {
            return "outlined";
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        if (this.canvas.layouter.isGenerating) {
            switch (this.frameType) {
                case "circle":
                    this.decoration.styles.shape = "circle";
                    break;
                case "square":
                    this.decoration.styles.shape = "rect";
                    if (this.canvas.getTheme().get("styleShape") != "rounded_rect") {
                        this.decoration.styles.cornerRadius = 0;
                    }
                    break;
                case "none":
                    this.assetElement.styles.fillColor = "auto";
                    this.assetElement.styles.iconScale = 0.8;
                    this.decoration.styles.shape = "none";
                    break;
            }

            if (this.asset && this.asset.get("hasSolidBackground") && this.asset.get("hasAlpha") == false) {
                if (styles.decoration) {
                    styles.decoration.fillColor = tinycolor(this.asset.get("imageBackgroundColor")).toHexString();
                    styles.decoration.strokeColor = "slide";
                    styles.decoration.strokeWidth = 2;
                } else {
                    styles.decoration = {
                        type: "frame",
                        shape: "rect",
                        fillColor: tinycolor(this.asset.get("imageBackgroundColor")).toHexString()
                    };
                }
            }
        }

        let aspectRatio;
        if (!this.assetElement.mediaSize || (this.decoration && this.decoration.styles.shape != "none")) {
            aspectRatio = 1;
        } else {
            aspectRatio = this.assetElement.mediaSize.aspectRatio;
        }

        let width = this.model.contentSize ? this.model.contentSize : size.width;
        size = new geom.Size(width, width / aspectRatio);

        if (!this.hasValidAsset) {
            let assetProps = children.setProps(this.assetElement, size);
            assetProps.bounds = new geom.Rect(0, 0, size);
            return { size };
        }

        let availableAssetSize = size.clone();
        if (this.frameType == "circle" && this.contentType == AssetType.LOGO) {
            let fit = fitRectInCircle(size.width, this.assetElement.mediaSize.width, this.assetElement.mediaSize.height);
            options.optimalFitSize = fit.deflate(4);
        }

        let assetProps = children.setProps(this.assetElement, availableAssetSize, options);
        assetProps.bounds = new geom.Rect(0, 0, size);

        const isMedia = this.contentType === AssetType.IMAGE || this.contentType === AssetType.LOGO || this.contentType === AssetType.VIDEO || this.contentType === AssetType.STOCK_VIDEO;
        const hasFrame = this.decoration && this.decoration.styles.type === "frame";
        if (this.hasValidAsset && isMedia && hasFrame) {
            assetProps.clipPath = getClipPath(this.decoration.styles.shape, assetProps.bounds, this.decoration.styles.cornerRadius);
        }

        return { size };
    }
}

export { ContentElement, LogoElement, AdjustableContentElement };
